// Feature: docs/features/coding-variance-report.md
// Feature: docs/features/coding-variance-preliminary-flag.md
// Feature: docs/features/structured-icd10-variance-analysis.md
// Feature: docs/features/icd10-variance-narrative-justification.md
// Feature: docs/features/coding-variance-dashboard-filters.md
// Feature: docs/features/icd10-dashboard-filters-stacking.md
// Feature: docs/features/coding-variance-net-new-codes.md
// Feature: docs/features/ionm-comprehensive-quality-check.md
import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box, Flex, Text, Badge, Spinner, Heading, Container,
  Table, Thead, Tbody, Tr, Th, Td, useToast, Button,
  Card, CardHeader, CardBody, HStack, Tabs, TabList, TabPanels, Tab, TabPanel,
  VStack, Divider, Input, FormControl, FormLabel, Switch,
  Stat, StatLabel, StatNumber, StatGroup, SimpleGrid, Grid, GridItem
} from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

export default function CodingVarianceDashboard() {
  const [reports, setReports] = useState([]);
  const [patterns, setPatterns] = useState({ 
    time_based: [], 
    unit_alignment: [], 
    cranial_bundling: [], 
    uncaptured_revenue: [],
    icd10_stats: {},
    icd10_complexity: []
  });
  const [loading, setLoading] = useState(true);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [hidePreliminary, setHidePreliminary] = useState(false);
  const [muscleGroupFilter, setMuscleGroupFilter] = useState('all'); // 'all', 'only', 'hide'
  const [unitModifierFilter, setUnitModifierFilter] = useState('all'); // 'all', 'only', 'hide'
  const [triageFilter, setTriageFilter] = useState('all'); // 'all', 'actionable', 'auto_resolved'
  
  // ICD-10 Filters
  const [primaryMismatchFilter, setPrimaryMismatchFilter] = useState('all'); // 'all', 'only', 'hide'
  const [revedyMoreFilter, setRevedyMoreFilter] = useState('all'); // 'all', 'only', 'hide'
  const [prospectMoreFilter, setProspectMoreFilter] = useState('all'); // 'all', 'only', 'hide'
  const [orderMismatchFilter, setOrderMismatchFilter] = useState('all'); // 'all', 'only', 'hide'
  const [revedySubsetMatchFilter, setRevedySubsetMatchFilter] = useState('all'); // 'all', 'only', 'hide'
  
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  
  // ICD-10 Search State
  const [prospectCode, setProspectCode] = useState('');
  const [revedyCode, setRevedyCode] = useState('');
  const [icd10SearchResults, setIcd10SearchResults] = useState([]);
  const [isSearchingIcd10, setIsSearchingIcd10] = useState(false);
  const [hasSearchedIcd10, setHasSearchedIcd10] = useState(false);
  
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const toast = useToast();

  const markdownStyles = {
    'p': { marginBottom: '0.5rem', whiteSpace: 'pre-wrap' },
    'ul': { paddingLeft: '1.5rem', marginBottom: '0.5rem' },
    'ol': { paddingLeft: '1.5rem', marginBottom: '0.5rem' },
    'li': { marginBottom: '0.25rem' },
    'strong': { fontWeight: 'bold' },
    'em': { fontStyle: 'italic' }
  };

  useEffect(() => {
    if (accessToken) {
      fetchReports();
      fetchPatterns();
    }
  }, [accessToken]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await callApi('/api/coding-variance/all', accessToken);
      const result = await response.json();
      if (result.status === 'success') {
        setReports(result.reports);
      } else {
        toast({
          title: "Error fetching reports",
          description: result.message || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching variance reports:', error);
      toast({
        title: "Network Error",
        description: "Could not connect to server.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleIcd10Search = async () => {
    if (!prospectCode && !revedyCode) {
      toast({
        title: "Input Required",
        description: "Please enter at least one code to search.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSearchingIcd10(true);
    setHasSearchedIcd10(true);
    try {
      const params = new URLSearchParams();
      if (prospectCode) params.append('prospect_code', prospectCode);
      if (revedyCode) params.append('revedy_code', revedyCode);

      const response = await callApi(`/api/coding-variance/icd10-search?${params.toString()}`, accessToken);
      const result = await response.json();
      
      if (result.status === 'success') {
        setIcd10SearchResults(result.reports);
      } else {
        toast({
          title: "Search Error",
          description: result.message || "Failed to search ICD-10 codes.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error searching ICD-10 codes:', error);
      toast({
        title: "Network Error",
        description: "Could not connect to server.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSearchingIcd10(false);
    }
  };

  const fetchPatterns = async () => {
    setLoadingPatterns(true);
    try {
      const response = await callApi('/api/coding-variance/patterns', accessToken);
      const result = await response.json();
      if (result.status === 'success') {
        setPatterns(result.patterns);
      }
    } catch (error) {
      console.error('Error fetching coding patterns:', error);
    } finally {
      setLoadingPatterns(false);
    }
  };

  const getCptLists = (variance) => {
    if (!variance) return { matches: [], mismatches: [], missing: [], extra: [] };
    
    // Helper to extract just the CPT code string
    const getCode = (item, source) => item[source]?.cpt_code;

    return {
      matches: (variance.exact_matches || []).map(i => getCode(i, 'prospect')),
      mismatches: [
        ...(variance.modifier_mismatches || []).map(i => getCode(i, 'prospect')),
        ...(variance.unit_mismatches || []).map(i => getCode(i, 'prospect'))
      ],
      missing: (variance.missing_in_prospect || []).map(i => getCode(i, 'revedy')),
      extra: (variance.extra_in_prospect || []).map(i => getCode(i, 'prospect'))
    };
  };

  const getIcd10Lists = (variance) => {
    if (!variance) return { matches: [], missing: [], extra: [] };
    return {
      matches: variance.exact_matches || [],
      missing: variance.missing_in_prospect || [],
      extra: variance.extra_in_prospect || []
    };
  };

  const renderModifiers = (modifiers) => {
    if (!modifiers || modifiers.length === 0) return <Text fontSize="xs" color="gray.400">None</Text>;
    return (
      <Flex flexWrap="wrap" gap={1}>
        {modifiers.map((mod, idx) => (
          <Badge key={idx} colorScheme="gray" variant="outline" fontSize="xs">{mod}</Badge>
        ))}
      </Flex>
    );
  };

  const renderCodeBadges = (codes, colorScheme, prefix = "") => {
    if (!codes || codes.length === 0) return null;
    return (
      <Flex flexWrap="wrap" gap={1}>
        {codes.map((code, idx) => (
          <Badge key={idx} colorScheme={colorScheme} variant="subtle" title={prefix}>
            {code}
          </Badge>
        ))}
      </Flex>
    );
  };

  // --- Sorting and Grouping Logic ---
  const { sortedCptReports, cptStats } = useMemo(() => {
    let varianceCount = 0;
    let autoResolvedCount = 0;
    let actionableCount = 0;
    let muscleGroupVarianceCount = 0;
    
    const processed = reports
      .filter(report => !hidePreliminary || !report.has_preliminary_codes)
      .filter(report => {
        if (muscleGroupFilter === 'only' && !report.has_muscle_group_variance) return false;
        if (muscleGroupFilter === 'hide' && report.has_muscle_group_variance) return false;
        
        const cpt = getCptLists(report.cpt_variance);
        const isUnitModOnly = cpt.mismatches.length > 0 && cpt.missing.length === 0 && cpt.extra.length === 0;
        
        if (unitModifierFilter === 'only' && !isUnitModOnly) return false;
        if (unitModifierFilter === 'hide' && isUnitModOnly) return false;
        
        const isAutoResolved = report.cpt_ai_evaluation?.triage_evaluation?.is_fully_auto_resolved === true;
        const hasVariance = cpt.mismatches.length > 0 || cpt.missing.length > 0 || cpt.extra.length > 0;
        const isActionable = hasVariance && !isAutoResolved;
        
        if (triageFilter === 'actionable' && hasVariance && !isActionable) return false;
        if (triageFilter === 'auto_resolved' && (!hasVariance || !isAutoResolved)) return false;
        
        return true;
      })
      .map(report => {
        const cpt = getCptLists(report.cpt_variance);
        const hasVariance = cpt.mismatches.length > 0 || cpt.missing.length > 0 || cpt.extra.length > 0;
        const isAutoResolved = report.cpt_ai_evaluation?.triage_evaluation?.is_fully_auto_resolved === true;
        const isActionable = hasVariance && !isAutoResolved;
        
        if (hasVariance) {
          varianceCount++;
          if (isAutoResolved) autoResolvedCount++;
          else actionableCount++;
        }
        
        if (report.has_muscle_group_variance) muscleGroupVarianceCount++;
        
        // Create a string of variance codes to group similar issues together
        const varianceCodes = [...new Set([...cpt.mismatches, ...cpt.missing, ...cpt.extra])].sort().join(',');
        
        return { ...report, cpt, hasVariance, isAutoResolved, isActionable, varianceCodes };
      });

    processed.sort((a, b) => {
      // 1. Triaged and Actionable first
      const aTriagedActionable = a.cpt_ai_evaluation?.triage_evaluation && a.isActionable;
      const bTriagedActionable = b.cpt_ai_evaluation?.triage_evaluation && b.isActionable;
      if (aTriagedActionable && !bTriagedActionable) return -1;
      if (!aTriagedActionable && bTriagedActionable) return 1;

      // 2. Triaged and Auto-Resolved second
      const aTriagedResolved = a.cpt_ai_evaluation?.triage_evaluation && a.isAutoResolved;
      const bTriagedResolved = b.cpt_ai_evaluation?.triage_evaluation && b.isAutoResolved;
      if (aTriagedResolved && !bTriagedResolved) return -1;
      if (!aTriagedResolved && bTriagedResolved) return 1;

      // 3. Untriaged Actionable third
      const aUntriagedActionable = !a.cpt_ai_evaluation?.triage_evaluation && a.isActionable;
      const bUntriagedActionable = !b.cpt_ai_evaluation?.triage_evaluation && b.isActionable;
      if (aUntriagedActionable && !bUntriagedActionable) return -1;
      if (!aUntriagedActionable && bUntriagedActionable) return 1;
      
      // 4. Group by variance codes
      if (a.varianceCodes < b.varianceCodes) return -1;
      if (a.varianceCodes > b.varianceCodes) return 1;
      
      // 5. Fallback to date
      return new Date(b.created_at) - new Date(a.created_at);
    });

    // Calculate total variances across ALL reports (ignoring the current filter)
    // so the stats at the top always show the true total.
    const totalMuscleGroupVariances = reports.filter(r => (!hidePreliminary || !r.has_preliminary_codes) && r.has_muscle_group_variance).length;
    
    let totalUnitModVariances = 0;
    let totalActionable = 0;
    let totalAutoResolved = 0;
    let totalTriaged = 0;
    
    const totalVariances = reports.filter(r => {
      if (hidePreliminary && r.has_preliminary_codes) return false;
      const cpt = getCptLists(r.cpt_variance);
      const hasVar = cpt.mismatches.length > 0 || cpt.missing.length > 0 || cpt.extra.length > 0;
      const isUnitModOnly = cpt.mismatches.length > 0 && cpt.missing.length === 0 && cpt.extra.length === 0;
      if (isUnitModOnly) totalUnitModVariances++;
      
      if (hasVar) {
        if (r.cpt_ai_evaluation?.triage_evaluation) {
          totalTriaged++;
          if (r.cpt_ai_evaluation.triage_evaluation.is_fully_auto_resolved === true) {
            totalAutoResolved++;
          } else {
            totalActionable++;
          }
        } else {
          // If it hasn't been triaged yet, it's actionable by default
          totalActionable++;
        }
      }
      
      return hasVar;
    }).length;

    return {
      sortedCptReports: processed,
      cptStats: {
        total: processed.length,
        variance: varianceCount,
        actionable: totalActionable,
        autoResolved: totalAutoResolved,
        triaged: totalTriaged,
        perfect: processed.length - varianceCount,
        totalMuscleGroupVariances,
        totalUnitModVariances,
        totalVariances
      }
    };
  }, [reports, hidePreliminary, muscleGroupFilter, unitModifierFilter, triageFilter]);

  const { sortedNetNewReports, netNewStats } = useMemo(() => {
    const processed = reports
      .filter(report => !hidePreliminary || !report.has_preliminary_codes)
      .filter(report => report.has_net_new_codes)
      .map(report => {
        const cpt = getCptLists(report.cpt_variance);
        return { ...report, cpt };
      });

    return {
      sortedNetNewReports: processed,
      netNewStats: {
        total: processed.length
      }
    };
  }, [reports, hidePreliminary]);

  const { sortedIcd10Reports, icd10Stats } = useMemo(() => {
    let varianceCount = 0;
    
    const processed = reports
      .filter(report => !hidePreliminary || !report.has_preliminary_codes)
      .filter(report => {
        const pCodes = report.prospect_icd10_data || [];
        const rCodes = report.revedy_icd10_data || [];
        const icd = getIcd10Lists(report.icd10_variance);
        
        // 1. Primary Mismatch
        const pPrimary = pCodes.length > 0 ? (pCodes[0].code || pCodes[0]).replace('.', '').toUpperCase() : '';
        const rPrimary = rCodes.length > 0 ? rCodes[0].replace('.', '').toUpperCase() : '';
        const isPrimaryMismatch = pPrimary && rPrimary && pPrimary !== rPrimary;
        
        if (primaryMismatchFilter === 'only' && !isPrimaryMismatch) return false;
        if (primaryMismatchFilter === 'hide' && isPrimaryMismatch) return false;

        // 2. Revedy Found More
        const isRevedyMore = rCodes.length > pCodes.length;
        if (revedyMoreFilter === 'only' && !isRevedyMore) return false;
        if (revedyMoreFilter === 'hide' && isRevedyMore) return false;

        // 3. Prospect Found More
        const isProspectMore = pCodes.length > rCodes.length;
        if (prospectMoreFilter === 'only' && !isProspectMore) return false;
        if (prospectMoreFilter === 'hide' && isProspectMore) return false;

        // 4. Order Mismatch
        const pCodesStr = pCodes.map(c => (c.code || c).replace('.', '').toUpperCase()).join(',');
        const rCodesStr = rCodes.map(c => c.replace('.', '').toUpperCase()).join(',');
        const isOrderMismatch = icd.missing.length === 0 && icd.extra.length === 0 && pCodesStr !== rCodesStr;
        
        if (orderMismatchFilter === 'only' && !isOrderMismatch) return false;
        if (orderMismatchFilter === 'hide' && isOrderMismatch) return false;

        // 5. Revedy Subset Match
        const isRevedySubsetMatch = icd.extra.length === 0 && icd.missing.length > 0;
        if (revedySubsetMatchFilter === 'only' && !isRevedySubsetMatch) return false;
        if (revedySubsetMatchFilter === 'hide' && isRevedySubsetMatch) return false;
        
        return true;
      })
      .map(report => {
        const icd = getIcd10Lists(report.icd10_variance);
        const hasVariance = icd.missing.length > 0 || icd.extra.length > 0;
        if (hasVariance) varianceCount++;
        
        const varianceCodes = [...new Set([...icd.missing, ...icd.extra])].sort().join(',');
        
        return { ...report, icd, hasVariance, varianceCodes };
      });

    processed.sort((a, b) => {
      if (a.hasVariance && !b.hasVariance) return -1;
      if (!a.hasVariance && b.hasVariance) return 1;
      if (a.varianceCodes < b.varianceCodes) return -1;
      if (a.varianceCodes > b.varianceCodes) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    // Calculate total variances across ALL reports (ignoring the current filter)
    const totalVariances = reports.filter(r => {
      if (hidePreliminary && r.has_preliminary_codes) return false;
      const icd = getIcd10Lists(r.icd10_variance);
      return icd.missing.length > 0 || icd.extra.length > 0;
    }).length;

    return {
      sortedIcd10Reports: processed,
      icd10Stats: {
        total: processed.length,
        variance: varianceCount,
        perfect: processed.length - varianceCount,
        totalVariances
      }
    };
  }, [reports, hidePreliminary, primaryMismatchFilter, revedyMoreFilter, prospectMoreFilter, orderMismatchFilter, revedySubsetMatchFilter]);

  const { sortedDenialReports, denialStats } = useMemo(() => {
    let totalLinesEvaluated = 0;
    let totalLinesPrevented = 0;

    const processed = reports
      .filter(report => !hidePreliminary || !report.has_preliminary_codes)
      .filter(report => report.payer_denial_evaluation && report.payer_denial_evaluation.status !== 'error')
      .map(report => {
        const evalData = report.payer_denial_evaluation;
        const lines = evalData.evaluated_lines || [];
        const preventedCount = lines.filter(l => l.would_revedy_make_difference).length;
        
        totalLinesEvaluated += lines.length;
        totalLinesPrevented += preventedCount;

        return { ...report, preventedCount, totalLines: lines.length };
      });

    processed.sort((a, b) => {
      // Sort by most prevented lines first
      if (a.preventedCount > b.preventedCount) return -1;
      if (a.preventedCount < b.preventedCount) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return {
      sortedDenialReports: processed,
      denialStats: {
        totalCases: processed.length,
        totalLinesEvaluated,
        totalLinesPrevented
      }
    };
  }, [reports, hidePreliminary]);

  // --- Render Helpers ---
  const renderNetNewCard = (report) => {
    const { cpt } = report;
    const aiDiffs = report.cpt_ai_evaluation?.differences || [];
    const { missing_in_prospect } = report.cpt_variance || {};
    
    const prospectCodes = Array.isArray(report.prospect_cpt_data) ? report.prospect_cpt_data.map(c => typeof c === 'object' ? (c.cpt_code || c.code) : c) : [];
    
    const filteredMissing = missing_in_prospect?.filter(item => {
      const code = item.revedy.cpt_code;
      if (prospectCodes.includes(code)) return false;
      if (['95941', 'G0453'].includes(code)) return false;
      if (code === '95955' && prospectCodes.includes('95822')) return false;
      if (code === '95870' && prospectCodes.includes('95861')) return false;
      if (code === '95867' && prospectCodes.includes('95868')) return false;
      return true;
    }) || [];
    
    // Deduplicate by CPT code
    const uniqueMissing = filteredMissing.filter((item, index, self) =>
      index === self.findIndex((t) => t.revedy.cpt_code === item.revedy.cpt_code)
    );

    if (uniqueMissing.length === 0) return null;

    return (
      <Card key={report.id} mb={6} shadow="sm" borderWidth="1px" borderColor="orange.200">
        <CardHeader bg="orange.50" py={3} borderBottomWidth="1px">
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Badge colorScheme="orange" fontSize="sm">
                Net New Codes Found
              </Badge>
              <Text fontWeight="bold">{report.external_case_id}</Text>
              <Text color="gray.600" fontSize="sm">{report.payer_type} - {report.carrier_name}</Text>
              <Badge colorScheme={report.claim_type === 'tech' ? 'purple' : 'blue'}>
                {report.claim_type?.toUpperCase() || 'PRO'}
              </Badge>
            </HStack>
            <HStack>
              <Text fontSize="xs" color="gray.500">{new Date(report.created_at).toLocaleDateString()}</Text>
              <Button 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                as="a"
                href={`/coding-variance?caseId=${report.external_case_id}&claimType=${report.claim_type || 'pro'}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Full Report
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody py={4}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {/* Left Side: Codes Overview */}
            <GridItem>
              <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Codes Overview</Heading>
              <Box bg="white" rounded="md" borderWidth="1px" borderColor="gray.200" overflow="hidden">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Source</Th>
                      <Th>Codes</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td fontWeight="medium" color="gray.600">Original Claim</Td>
                      <Td>{renderCodeBadges([...new Set(Array.isArray(report.prospect_cpt_data) ? report.prospect_cpt_data.map(c => typeof c === 'object' ? (c.cpt_code || c.code) : c) : [])], "gray")}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="medium" color="blue.600">Revedy AI</Td>
                      <Td>{renderCodeBadges([...new Set(Array.isArray(report.revedy_cpt_data) ? report.revedy_cpt_data.map(c => typeof c === 'object' ? (c.cpt_code || c.code) : c) : [])], "blue")}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            </GridItem>

            {/* Right Side: Net New Details */}
            <GridItem borderLeftWidth={{ base: "0", lg: "1px" }} pl={{ base: 0, lg: 6 }}>
              <Heading size="xs" color="orange.600" textTransform="uppercase" mb={3}>Net New Codes Explained</Heading>
              <VStack align="stretch" spacing={4}>
                {uniqueMissing.map((item, idx) => {
                  const code = item.revedy.cpt_code;
                  // Find the AI explanation for this specific code
                  const explanation = aiDiffs.find(diff => diff.revedy_cpt?.includes(code))?.explanation;
                  
                  return (
                    <Box key={idx} p={3} bg="orange.50" rounded="md" borderWidth="1px" borderColor="orange.200">
                      <Flex justify="space-between" align="center" mb={2}>
                        <HStack>
                          <Badge colorScheme="orange" fontSize="md" px={2} py={1}>{code}</Badge>
                          {item.revedy.modifiers?.length > 0 && (
                            <Text fontSize="xs" color="gray.600">Mods: {item.revedy.modifiers.join(', ')}</Text>
                          )}
                          {item.revedy.units > 1 && (
                            <Text fontSize="xs" color="gray.600">Units: {item.revedy.units}</Text>
                          )}
                        </HStack>
                      </Flex>
                      {explanation ? (
                        <Text fontSize="sm" color="gray.700">{explanation}</Text>
                      ) : (
                        <Text fontSize="sm" color="gray.500" fontStyle="italic">No AI explanation available for this code.</Text>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </GridItem>
          </SimpleGrid>
        </CardBody>
      </Card>
    );
  };

  const renderCptCard = (report) => {
    const { cpt, hasVariance, isAutoResolved, isActionable } = report;
    const aiDiffs = report.cpt_ai_evaluation?.differences || [];
    const triageEval = report.cpt_ai_evaluation?.triage_evaluation;
    const { exact_matches, modifier_mismatches, unit_mismatches, missing_in_prospect, extra_in_prospect } = report.cpt_variance || {};

    let cardBorderColor = "green.200";
    let cardBgColor = "green.50";
    let badgeColorScheme = "green";
    let badgeText = "Perfect Match";

    if (isActionable) {
      cardBorderColor = "orange.200";
      cardBgColor = "orange.50";
      badgeColorScheme = "orange";
      badgeText = "Actionable Variance";
    } else if (isAutoResolved) {
      cardBorderColor = "blue.200";
      cardBgColor = "blue.50";
      badgeColorScheme = "blue";
      badgeText = "Auto-Resolved Variance";
    }

    return (
      <Card key={report.id} mb={6} shadow="sm" borderWidth="1px" borderColor={cardBorderColor}>
        <CardHeader bg={cardBgColor} py={3} borderBottomWidth="1px">
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Badge colorScheme={badgeColorScheme} fontSize="sm">
                {badgeText}
              </Badge>
              <Text fontWeight="bold">{report.external_case_id}</Text>
              <Text color="gray.600" fontSize="sm">{report.payer_type} - {report.carrier_name}</Text>
              <Badge colorScheme={report.claim_type === 'tech' ? 'purple' : 'blue'}>
                {report.claim_type?.toUpperCase() || 'PRO'}
              </Badge>
              {report.has_preliminary_codes && (
                <Badge colorScheme="orange" variant="solid" fontSize="xs">
                  ⚠️ Needs Review
                </Badge>
              )}
              {report.quality_check?.status === 'needs_review' && (
                <Badge colorScheme="red" variant="solid" fontSize="xs">
                  🚨 Quality Check Failed
                </Badge>
              )}
            </HStack>
            <HStack>
              <Text fontSize="xs" color="gray.500">{new Date(report.created_at).toLocaleDateString()}</Text>
              <Button 
                size="sm" 
                colorScheme="purple" 
                variant="outline"
                as="a"
                href={`/ionm-strict-analysis/${report.external_case_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Medical Necessity
              </Button>
              <Button 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                as="a"
                href={`/coding-variance?caseId=${report.external_case_id}&claimType=${report.claim_type || 'pro'}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Full Report
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody>
          {report.quality_check?.status === 'needs_review' && report.quality_check?.notes && (
            <Box mb={6} p={4} bg="red.50" rounded="md" borderWidth="1px" borderColor="red.200">
              <Text fontWeight="bold" color="red.800" mb={2}>Quality Check Discrepancies</Text>
              <Box fontSize="sm" color="red.900" whiteSpace="pre-wrap">
                <ReactMarkdown>{report.quality_check.notes}</ReactMarkdown>
              </Box>
            </Box>
          )}
          <Grid templateColumns={{ base: "1fr", lg: "3fr 2fr" }} gap={8}>
            {/* Left Side: Deterministic Data */}
            <GridItem overflowX="auto">
              <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Deterministic Variance</Heading>
              <Box borderWidth="1px" rounded="md" overflow="hidden">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th borderRightWidth="1px"></Th>
                      <Th colSpan={3} textAlign="center" borderRightWidth="1px" bg="gray.100">Claim Data (CMS 1500)</Th>
                      <Th colSpan={3} textAlign="center" bg="blue.50">Revedy Output</Th>
                    </Tr>
                    <Tr>
                      {/* Claim */}
                      <Th>CPT</Th>
                      <Th>Modifiers</Th>
                      <Th borderRightWidth="1px">Units</Th>
                      {/* Revedy */}
                      <Th bg="blue.50">CPT</Th>
                      <Th bg="blue.50">Modifiers</Th>
                      <Th bg="blue.50">Units</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* Exact Matches */}
                    {exact_matches?.map((item, idx) => (
                      <Tr key={`exact-${idx}`} bg="green.50">
                        <Td borderRightWidth="1px"><Badge colorScheme="green">Match</Badge></Td>
                        <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                        <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                        <Td borderRightWidth="1px">{item.prospect.units}</Td>
                        <Td fontWeight="bold">
                          {item.revedy.cpt_code}
                          {item.revedy.type === 'preliminary' && (
                            <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                          )}
                        </Td>
                        <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                        <Td>{item.revedy.units}</Td>
                      </Tr>
                    ))}

                    {/* Modifier Mismatches */}
                    {modifier_mismatches?.map((item, idx) => (
                      <Tr key={`mod-mis-${idx}`} bg="yellow.50">
                        <Td borderRightWidth="1px"><Badge colorScheme="yellow">Mod Mismatch</Badge></Td>
                        <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                        <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                        <Td borderRightWidth="1px">{item.prospect.units}</Td>
                        <Td fontWeight="bold">
                          {item.revedy.cpt_code}
                          {item.revedy.type === 'preliminary' && (
                            <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                          )}
                        </Td>
                        <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                        <Td>{item.revedy.units}</Td>
                      </Tr>
                    ))}

                    {/* Unit Mismatches */}
                    {unit_mismatches?.map((item, idx) => (
                      <Tr key={`unit-mis-${idx}`} bg="orange.50">
                        <Td borderRightWidth="1px"><Badge colorScheme="orange">Unit Mismatch</Badge></Td>
                        <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                        <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                        <Td borderRightWidth="1px" fontWeight="bold" color="red.600">{item.prospect.units}</Td>
                        <Td fontWeight="bold">
                          {item.revedy.cpt_code}
                          {item.revedy.type === 'preliminary' && (
                            <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                          )}
                        </Td>
                        <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                        <Td fontWeight="bold" color="green.600">{item.revedy.units}</Td>
                      </Tr>
                    ))}

                    {/* Extra in Prospect (Claim Only) */}
                    {extra_in_prospect?.map((item, idx) => (
                      <Tr key={`extra-${idx}`} bg="red.50">
                        <Td borderRightWidth="1px"><Badge colorScheme="red">Claim Only</Badge></Td>
                        <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                        <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                        <Td borderRightWidth="1px">{item.prospect.units}</Td>
                        <Td color="gray.400">-</Td>
                        <Td color="gray.400">-</Td>
                        <Td color="gray.400">-</Td>
                      </Tr>
                    ))}

                    {/* Missing in Prospect (Revedy Only) */}
                    {missing_in_prospect?.map((item, idx) => (
                      <Tr key={`missing-${idx}`} bg="blue.50">
                        <Td borderRightWidth="1px"><Badge colorScheme="blue">Revedy Only</Badge></Td>
                        <Td color="gray.400">-</Td>
                        <Td color="gray.400">-</Td>
                        <Td borderRightWidth="1px" color="gray.400">-</Td>
                        <Td fontWeight="bold">
                          {item.revedy.cpt_code}
                          {item.revedy.type === 'preliminary' && (
                            <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                          )}
                        </Td>
                        <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                        <Td>{item.revedy.units}</Td>
                      </Tr>
                    ))}

                    {(!exact_matches?.length && !modifier_mismatches?.length && !unit_mismatches?.length && !missing_in_prospect?.length && !extra_in_prospect?.length) && (
                      <Tr>
                        <Td colSpan={7} textAlign="center" py={4} color="gray.500">No CPT data available.</Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </GridItem>

            {/* Right Side: AI Evaluation */}
            <GridItem borderLeftWidth={{ base: "0", lg: "1px" }} pl={{ base: 0, lg: 8 }} pt={{ base: 4, lg: 0 }}>
              <Heading size="xs" color="purple.600" textTransform="uppercase" mb={3}>AI Structured Analysis</Heading>
              
              {triageEval && triageEval.triage_summary && (
                <Box p={3} bg={triageEval.is_fully_auto_resolved ? 'blue.50' : 'orange.50'} rounded="md" borderWidth="1px" borderColor={triageEval.is_fully_auto_resolved ? 'blue.200' : 'orange.200'} mb={4}>
                  <Text fontSize="xs" fontWeight="bold" color={triageEval.is_fully_auto_resolved ? 'blue.800' : 'orange.800'} mb={1}>
                    TRIAGE SUMMARY:
                  </Text>
                  <Text fontSize="sm" color={triageEval.is_fully_auto_resolved ? 'blue.900' : 'orange.900'}>
                    {triageEval.triage_summary}
                  </Text>
                </Box>
              )}

              {aiDiffs.length > 0 ? (
                <VStack align="stretch" spacing={4}>
                  {aiDiffs.map((diff, idx) => (
                    <Box key={idx} p={3} bg="gray.50" rounded="md" borderWidth="1px" borderColor="gray.200">
                      <Flex justify="space-between" mb={2}>
                        <Box>
                          <Text fontSize="2xs" fontWeight="bold" color="gray.500">CLAIM</Text>
                          <Text fontSize="sm" fontWeight="medium">{diff.claim_cpt}</Text>
                        </Box>
                        <Box textAlign="right">
                          <Text fontSize="2xs" fontWeight="bold" color="gray.500">REVEDY</Text>
                          <Text fontSize="sm" fontWeight="medium">{diff.revedy_cpt}</Text>
                        </Box>
                      </Flex>
                      <Flex align="center" mb={2} gap={2}>
                        <Badge 
                          colorScheme={
                            diff.who_was_right?.toLowerCase() === 'revedy' ? 'blue' : 
                            diff.who_was_right?.toLowerCase() === 'claim' ? 'orange' : 
                            diff.who_was_right?.toLowerCase() === 'both' ? 'green' : 'red'
                          }
                          fontSize="2xs"
                        >
                          RIGHT: {diff.who_was_right?.toUpperCase()}
                        </Badge>
                      </Flex>
                      <Text fontSize="xs" color="gray.700">{diff.explanation}</Text>
                    </Box>
                  ))}
                </VStack>
              ) : hasVariance ? (
                <Text fontSize="sm" color="gray.500" fontStyle="italic">AI analysis not generated for these differences.</Text>
              ) : (
                <Text fontSize="sm" color="green.600">No differences to analyze.</Text>
              )}
            </GridItem>
          </Grid>
        </CardBody>
      </Card>
    );
  };

  const renderDenialCard = (report) => {
    const evalData = report.payer_denial_evaluation;
    if (!evalData) return null;

    const prospectCpts = Array.isArray(report.prospect_cpt_data) ? report.prospect_cpt_data.map(c => typeof c === 'object' ? (c.cpt_code || c.code) : c) : [];
    const revedyCpts = Array.isArray(report.revedy_cpt_data) ? report.revedy_cpt_data.map(c => typeof c === 'object' ? (c.cpt_code || c.code) : c) : [];
    
    const prospectIcd10s = Array.isArray(report.prospect_icd10_data) ? report.prospect_icd10_data.map(c => typeof c === 'object' ? (c.code || c) : c) : [];
    const revedyIcd10s = report.revedy_icd10_data || [];

    return (
      <Card key={report.id} mb={6} shadow="sm" borderWidth="1px" borderColor={report.preventedCount > 0 ? "green.200" : "gray.200"}>
        <CardHeader bg={report.preventedCount > 0 ? "green.50" : "gray.50"} py={3} borderBottomWidth="1px">
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Badge colorScheme={report.preventedCount > 0 ? "green" : "gray"} fontSize="sm">
                {report.preventedCount > 0 ? "Potential Mitigation Identified" : "No Change in Outcome"}
              </Badge>
              <Text fontWeight="bold">{report.external_case_id}</Text>
              <Text color="gray.600" fontSize="sm">{report.payer_type} - {report.carrier_name}</Text>
              <Badge colorScheme={report.claim_type === 'tech' ? 'purple' : 'blue'}>
                {report.claim_type?.toUpperCase() || 'PRO'}
              </Badge>
            </HStack>
            <HStack>
              <Text fontSize="xs" color="gray.500">{new Date(report.created_at).toLocaleDateString()}</Text>
              <Button 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                as="a"
                href={`/coding-variance?caseId=${report.external_case_id}&claimType=${report.claim_type || 'pro'}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Full Report
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={6}>
            {/* AI Summary */}
            <Box p={4} bg="blue.50" rounded="md" borderWidth="1px" borderColor="blue.100">
              <Text fontWeight="bold" color="blue.800" mb={2}>AI Impact Summary</Text>
              <Text fontSize="sm" color="blue.900">{evalData.overall_summary}</Text>
            </Box>

            {/* Context Grid */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>CPT Context</Text>
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <Box p={2} bg="gray.50" rounded="md" borderWidth="1px">
                    <Text fontSize="2xs" color="gray.500" mb={1}>CLAIM BILLED</Text>
                    {renderCodeBadges([...new Set(prospectCpts)], "gray")}
                  </Box>
                  <Box p={2} bg="blue.50" rounded="md" borderWidth="1px">
                    <Text fontSize="2xs" color="blue.600" mb={1}>REVEDY RECOMMENDED</Text>
                    {renderCodeBadges([...new Set(revedyCpts)], "blue")}
                  </Box>
                </Grid>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>ICD-10 Context</Text>
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <Box p={2} bg="gray.50" rounded="md" borderWidth="1px">
                    <Text fontSize="2xs" color="gray.500" mb={1}>CLAIM BILLED</Text>
                    {renderCodeBadges([...new Set(prospectIcd10s)], "gray")}
                  </Box>
                  <Box p={2} bg="blue.50" rounded="md" borderWidth="1px">
                    <Text fontSize="2xs" color="blue.600" mb={1}>REVEDY RECOMMENDED</Text>
                    {renderCodeBadges([...new Set(revedyIcd10s)], "blue")}
                  </Box>
                </Grid>
              </Box>
            </SimpleGrid>

            {/* Detailed Table */}
            <Box overflowX="auto" borderWidth="1px" rounded="md">
              <Table variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>CPT Code</Th>
                    <Th>Denial Reason (EOB)</Th>
                    <Th>AI Mapping & Pathway</Th>
                    <Th>Revedy Proposed Changes</Th>
                    <Th>Impact</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(evalData.evaluated_lines || []).map((line, idx) => {
                    // Find the corresponding line item from the raw EOB data to get the mapping info
                    const rawLineItem = report.eob_data?.line_items?.find(item => item.cpt_code === line.cpt_code);
                    
                    return (
                      <Tr key={idx}>
                        <Td fontWeight="bold" verticalAlign="top">
                          {line.cpt_code}
                          <Box mt={1}>{renderModifiers(line.modifiers_billed)}</Box>
                        </Td>
                        <Td fontSize="sm" color="red.600" verticalAlign="top" maxW="250px">
                          {line.denial_reason}
                        </Td>
                        <Td verticalAlign="top" maxW="200px">
                          <VStack align="start" spacing={1}>
                            {rawLineItem?.mapped_carcs && rawLineItem.mapped_carcs.length > 0 ? (
                              rawLineItem.mapped_carcs.map((mc, i) => (
                                <Badge key={i} colorScheme="purple" title={mc.reasoning}>
                                  CARC: {mc.code} ({(mc.confidence * 100).toFixed(0)}%)
                                </Badge>
                              ))
                            ) : (
                              <Text fontSize="xs" color="gray.400">-</Text>
                            )}
                            {rawLineItem?.dispute_pathway && rawLineItem.dispute_pathway !== 'standard_appeal' && (
                              <Badge colorScheme="red">{rawLineItem.dispute_pathway.toUpperCase()}</Badge>
                            )}
                            {rawLineItem?.legal_mandate && (
                              <Badge colorScheme="blue">{rawLineItem.legal_mandate}</Badge>
                            )}
                          </VStack>
                        </Td>
                        <Td fontSize="sm" color="blue.600" verticalAlign="top" maxW="250px">
                          {line.revedy_proposed_changes}
                        </Td>
                        <Td verticalAlign="top">
                          <VStack align="start" spacing={1}>
                            <Badge colorScheme={line.would_revedy_make_difference ? "green" : "gray"}>
                              {line.would_revedy_make_difference ? "Potential Mitigation" : "No Change"}
                            </Badge>
                            <Text fontSize="xs" color="gray.600" maxW="300px" whiteSpace="normal">
                              {line.explanation}
                            </Text>
                          </VStack>
                        </Td>
                      </Tr>
                    );
                  })}
                  {(!evalData.evaluated_lines || evalData.evaluated_lines.length === 0) && (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={4} color="gray.500">No denied lines evaluated.</Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  const renderIcd10Card = (report) => {
    const { icd, hasVariance } = report;
    const aiEval = report.icd10_ai_evaluation;

    return (
      <Card key={report.id} mb={6} shadow="sm" borderWidth="1px" borderColor={hasVariance ? "orange.200" : "green.200"}>
        <CardHeader bg={hasVariance ? "orange.50" : "green.50"} py={3} borderBottomWidth="1px">
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Badge colorScheme={hasVariance ? "orange" : "green"} fontSize="sm">
                {hasVariance ? "Variance Detected" : "Perfect Match"}
              </Badge>
              <Text fontWeight="bold">{report.external_case_id}</Text>
              <Text color="gray.600" fontSize="sm">{report.payer_type} - {report.carrier_name}</Text>
              <Badge colorScheme={report.claim_type === 'tech' ? 'purple' : 'blue'}>
                {report.claim_type?.toUpperCase() || 'PRO'}
              </Badge>
              {report.has_preliminary_codes && (
                <Badge colorScheme="orange" variant="solid" fontSize="xs">
                  ⚠️ Needs Review
                </Badge>
              )}
              {report.quality_check?.status === 'needs_review' && (
                <Badge colorScheme="red" variant="solid" fontSize="xs">
                  🚨 Quality Check Failed
                </Badge>
              )}
            </HStack>
            <HStack>
              <Text fontSize="xs" color="gray.500">{new Date(report.created_at).toLocaleDateString()}</Text>
              <Button 
                size="sm" 
                colorScheme="purple" 
                variant="outline"
                as="a"
                href={`/ionm-strict-analysis/${report.external_case_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Medical Necessity
              </Button>
              <Button 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                as="a"
                href={`/coding-variance?caseId=${report.external_case_id}&claimType=${report.claim_type || 'pro'}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Full Report
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody>
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
            {/* Left Side: Deterministic Data */}
            <GridItem>
              <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Deterministic Variance</Heading>
              
              <Grid templateColumns={report.legacy_mn_icd10_data && report.legacy_mn_icd10_data.length > 0 ? "1fr 1fr 1fr" : "1fr 1fr"} gap={4} mb={4}>
                <Card variant="outline" borderColor="gray.200" shadow="none">
                  <CardHeader bg="gray.50" py={2} px={3} borderBottomWidth="1px">
                    <Text fontSize="xs" fontWeight="bold" color="gray.700">Claim Billed</Text>
                  </CardHeader>
                  <CardBody p={2}>
                    <VStack align="stretch" spacing={2}>
                      {(Array.isArray(report.prospect_icd10_data) ? report.prospect_icd10_data : []).map((item, idx) => {
                        const code = item.code || item;
                        const normalizedCode = code.replace('.', '').toUpperCase();
                        const isMatch = icd.matches.includes(normalizedCode);
                        const color = isMatch ? "green" : "yellow";
                        
                        // Check if explicitly in text
                        const inDoc = report.icd10_variance?.codes_in_text?.includes(normalizedCode);

                        return (
                          <Flex key={idx} justify="space-between" align="center" p={1.5} bg={`${color}.50`} rounded="md" borderWidth="1px" borderColor={`${color}.200`}>
                            <Text fontSize="xs" fontWeight="bold" color={`${color}.700`}>Order {idx + 1}</Text>
                            <HStack>
                              {inDoc && <Badge colorScheme="green" fontSize="2xs" variant="outline">In Doc</Badge>}
                              <Badge colorScheme={color} fontSize="sm">{code}</Badge>
                            </HStack>
                          </Flex>
                        );
                      })}
                      {(!report.prospect_icd10_data || report.prospect_icd10_data.length === 0) && (
                        <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No codes billed</Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                <Card variant="outline" borderColor="gray.200" shadow="none">
                  <CardHeader bg="blue.50" py={2} px={3} borderBottomWidth="1px" borderColor="blue.100">
                    <Text fontSize="xs" fontWeight="bold" color="blue.800">Revedy Recommended</Text>
                  </CardHeader>
                  <CardBody p={2}>
                    <VStack align="stretch" spacing={2}>
                      {(Array.isArray(report.revedy_icd10_data) ? report.revedy_icd10_data : []).map((code, idx) => {
                        const normalizedCode = code.replace('.', '').toUpperCase();
                        const isMatch = icd.matches.includes(normalizedCode);
                        const color = isMatch ? "green" : "blue";
                        
                        // Check if explicitly in text
                        const inDoc = report.icd10_variance?.codes_in_text?.includes(normalizedCode);

                        return (
                          <Flex key={idx} justify="space-between" align="center" p={1.5} bg={`${color}.50`} rounded="md" borderWidth="1px" borderColor={`${color}.200`}>
                            <Text fontSize="xs" fontWeight="bold" color={`${color}.700`}>Order {idx + 1}</Text>
                            <HStack>
                              {inDoc && <Badge colorScheme="green" fontSize="2xs" variant="outline">In Doc</Badge>}
                              <Badge colorScheme={color} fontSize="sm">{code}</Badge>
                            </HStack>
                          </Flex>
                        );
                      })}
                      {(!report.revedy_icd10_data || report.revedy_icd10_data.length === 0) && (
                        <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>No codes recommended</Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                {/* Legacy MN Data Display */}
                {report.legacy_mn_icd10_data && report.legacy_mn_icd10_data.length > 0 && (
                  <Card variant="outline" borderColor="gray.200" shadow="none">
                    <CardHeader bg="gray.50" py={2} px={3} borderBottomWidth="1px">
                      <Text fontSize="xs" fontWeight="bold" color="gray.600">Legacy Analysis</Text>
                    </CardHeader>
                    <CardBody p={2}>
                      <VStack align="stretch" spacing={2}>
                        {report.legacy_mn_icd10_data.map((code, idx) => (
                          <Flex key={idx} justify="space-between" align="center" p={1.5} bg="gray.50" rounded="md" borderWidth="1px" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="bold" color="gray.500">Order {idx + 1}</Text>
                            <Badge colorScheme="gray" variant="outline" fontSize="sm">{code}</Badge>
                          </Flex>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>
                )}
              </Grid>

              <VStack align="stretch" spacing={3} mt={4} pt={4} borderTopWidth="1px" borderColor="gray.100">
                {icd.matches.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="green.600" mb={1}>Exact Matches</Text>
                    {renderCodeBadges(icd.matches, "green")}
                  </Box>
                )}
                {icd.missing.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="blue.600" mb={1}>Revedy Only (Missing in Claim)</Text>
                    {renderCodeBadges(icd.missing, "blue")}
                  </Box>
                )}
                {icd.extra.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="yellow.600" mb={1}>Claim Only (Extra in Claim)</Text>
                    {renderCodeBadges(icd.extra, "yellow")}
                  </Box>
                )}
                {!hasVariance && icd.matches.length === 0 && (
                  <Text fontSize="sm" color="gray.400">No ICD-10 data available.</Text>
                )}
              </VStack>
              
              {/* ICD-10 Optimization Section */}
              {report.icd10_optimization && (report.icd10_optimization.prospect || report.icd10_optimization.revedy) && (
                <Box mt={6} pt={4} borderTopWidth="1px" borderColor="gray.200">
                  <Heading size="xs" color="teal.600" textTransform="uppercase" mb={3}>Payer Rules Optimization</Heading>
                  <Grid templateColumns="1fr 1fr" gap={4}>
                    {report.icd10_optimization.prospect && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Claim Billed Optimization</Text>
                        
                        {/* Critical Warnings */}
                        {report.icd10_optimization.prospect.warnings?.critical?.length > 0 && (
                          <VStack align="stretch" spacing={1} mb={2}>
                            {report.icd10_optimization.prospect.warnings.critical.map((w, i) => (
                              <Text key={i} fontSize="xs" color="red.600" fontWeight="bold">
                                ⚠️ CRITICAL: {w}
                              </Text>
                            ))}
                          </VStack>
                        )}
                        
                        {/* Notes */}
                        {report.icd10_optimization.prospect.warnings?.notes?.length > 0 && (
                          <VStack align="stretch" spacing={1} mb={3}>
                            {report.icd10_optimization.prospect.warnings.notes.map((w, i) => (
                              <Text key={i} fontSize="xs" color="gray.500" fontStyle="italic">
                                ℹ️ {w}
                              </Text>
                            ))}
                          </VStack>
                        )}
                        
                        {(!report.icd10_optimization.prospect.warnings?.critical?.length && !report.icd10_optimization.prospect.warnings?.notes?.length) && (
                          <Text fontSize="xs" color="green.600" mb={3}>✓ No payer rule violations</Text>
                        )}
                        
                        <Text fontSize="xs" color="gray.500" mb={1}>Optimal Order:</Text>
                        <VStack align="stretch" spacing={2}>
                          {(report.icd10_optimization.prospect.optimal_codes || []).map((item, idx) => (
                            <Box key={idx} p={2} bg="teal.50" rounded="md" borderWidth="1px" borderColor="teal.100">
                              <HStack mb={1}>
                                <Badge colorScheme="teal">{item.code || item}</Badge>
                              </HStack>
                              {item.reason && <Text fontSize="2xs" color="teal.700">{item.reason}</Text>}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                    
                    {report.icd10_optimization.revedy && (
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Revedy Optimization</Text>
                        
                        {/* Critical Warnings */}
                        {report.icd10_optimization.revedy.warnings?.critical?.length > 0 && (
                          <VStack align="stretch" spacing={1} mb={2}>
                            {report.icd10_optimization.revedy.warnings.critical.map((w, i) => (
                              <Text key={i} fontSize="xs" color="red.600" fontWeight="bold">
                                ⚠️ CRITICAL: {w}
                              </Text>
                            ))}
                          </VStack>
                        )}
                        
                        {/* Notes */}
                        {report.icd10_optimization.revedy.warnings?.notes?.length > 0 && (
                          <VStack align="stretch" spacing={1} mb={3}>
                            {report.icd10_optimization.revedy.warnings.notes.map((w, i) => (
                              <Text key={i} fontSize="xs" color="gray.500" fontStyle="italic">
                                ℹ️ {w}
                              </Text>
                            ))}
                          </VStack>
                        )}
                        
                        {(!report.icd10_optimization.revedy.warnings?.critical?.length && !report.icd10_optimization.revedy.warnings?.notes?.length) && (
                          <Text fontSize="xs" color="green.600" mb={3}>✓ No payer rule violations</Text>
                        )}
                        
                        <Text fontSize="xs" color="gray.500" mb={1}>Optimal Order:</Text>
                        <VStack align="stretch" spacing={2}>
                          {(report.icd10_optimization.revedy.optimal_codes || []).map((item, idx) => (
                            <Box key={idx} p={2} bg="teal.50" rounded="md" borderWidth="1px" borderColor="teal.100">
                              <HStack mb={1}>
                                <Badge colorScheme="teal">{item.code || item}</Badge>
                              </HStack>
                              {item.reason && <Text fontSize="2xs" color="teal.700">{item.reason}</Text>}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </Grid>
                </Box>
              )}
            </GridItem>

            {/* Right Side: AI Evaluation */}
            <GridItem borderLeftWidth={{ base: "0", lg: "1px" }} pl={{ base: 0, lg: 8 }} pt={{ base: 4, lg: 0 }}>
              <Heading size="xs" color="purple.600" textTransform="uppercase" mb={3}>AI Analysis</Heading>
              {aiEval && aiEval.discrepancy_summary ? (
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.700">Summary</Text>
                    <Text fontSize="sm">{aiEval.discrepancy_summary}</Text>
                  </Box>

                  {aiEval.revedy_justification && (
                    <Box p={3} bg="blue.50" rounded="md" borderWidth="1px" borderColor="blue.100">
                      <Text fontSize="xs" fontWeight="bold" color="blue.800" mb={2}>Revedy Justification</Text>
                      <Box fontSize="xs" color="blue.900" sx={markdownStyles}>
                        <ReactMarkdown>{aiEval.revedy_justification}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}

                  {aiEval.claim_rationale_hypothesis && (
                    <Box p={3} bg="orange.50" rounded="md" borderWidth="1px" borderColor="orange.100">
                      <Text fontSize="xs" fontWeight="bold" color="orange.800" mb={2}>Claim Rationale Hypothesis</Text>
                      <Box fontSize="xs" color="orange.900" sx={markdownStyles}>
                        <ReactMarkdown>{aiEval.claim_rationale_hypothesis}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                </VStack>
              ) : hasVariance ? (
                <Text fontSize="sm" color="gray.500" fontStyle="italic">AI analysis not generated.</Text>
              ) : (
                <Text fontSize="sm" color="green.600">No differences to analyze.</Text>
              )}
            </GridItem>
          </Grid>
        </CardBody>
      </Card>
    );
  };



  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Container maxW="container.xl" py={6}>
        
        <Flex justify="space-between" align="flex-start" mb={6} direction={{ base: "column", xl: "row" }} gap={4}>
          <Box minW="300px">
            <Heading size="lg" mb={1}>Coding Variance Dashboard</Heading>
            <Text color="gray.600">
              Overview of all generated deterministic comparisons and coding patterns.
            </Text>
          </Box>
          <Flex wrap="wrap" gap={3} justify={{ base: "flex-start", xl: "flex-end" }} align="center" flex="1">
            {activeTabIndex === 0 && (
              <>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    CPT Filter:
                  </FormLabel>
                  <Box as="select" 
                    value={muscleGroupFilter} 
                    onChange={(e) => setMuscleGroupFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="all">All CPT Variances</option>
                    <option value="only">95861/95870 Only</option>
                    <option value="hide">Hide 95861/95870</option>
                  </Box>
                </FormControl>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    Unit/Mod Only:
                  </FormLabel>
                  <Box as="select" 
                    value={unitModifierFilter} 
                    onChange={(e) => setUnitModifierFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="all">Show All</option>
                    <option value="only">View Unit/Mod Only</option>
                    <option value="hide">Hide Unit/Mod Only</option>
                  </Box>
                </FormControl>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    Triage:
                  </FormLabel>
                  <Box as="select" 
                    value={triageFilter} 
                    onChange={(e) => setTriageFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="actionable">Actionable Only</option>
                    <option value="auto_resolved">Auto-Resolved Only</option>
                    <option value="all">Show All Variances</option>
                  </Box>
                </FormControl>
              </>
            )}
            
            {activeTabIndex === 2 && (
              <>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    Primary Mismatch:
                  </FormLabel>
                  <Box as="select" 
                    value={primaryMismatchFilter} 
                    onChange={(e) => setPrimaryMismatchFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="all">Show All</option>
                    <option value="only">Show Only</option>
                    <option value="hide">Hide</option>
                  </Box>
                </FormControl>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    Revedy More:
                  </FormLabel>
                  <Box as="select" 
                    value={revedyMoreFilter} 
                    onChange={(e) => setRevedyMoreFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="all">Show All</option>
                    <option value="only">Show Only</option>
                    <option value="hide">Hide</option>
                  </Box>
                </FormControl>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    Prospect More:
                  </FormLabel>
                  <Box as="select" 
                    value={prospectMoreFilter} 
                    onChange={(e) => setProspectMoreFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="all">Show All</option>
                    <option value="only">Show Only</option>
                    <option value="hide">Hide</option>
                  </Box>
                </FormControl>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    Order Mismatch:
                  </FormLabel>
                  <Box as="select" 
                    value={orderMismatchFilter} 
                    onChange={(e) => setOrderMismatchFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="all">Show All</option>
                    <option value="only">Show Only</option>
                    <option value="hide">Hide</option>
                  </Box>
                </FormControl>
                <FormControl display="flex" alignItems="center" bg="white" p={1} rounded="md" borderWidth="1px" borderColor="gray.200" w="auto">
                  <FormLabel mb="0" fontSize="sm" color="gray.600" pl={2} whiteSpace="nowrap">
                    Subset Match:
                  </FormLabel>
                  <Box as="select" 
                    value={revedySubsetMatchFilter} 
                    onChange={(e) => setRevedySubsetMatchFilter(e.target.value)}
                    fontSize="sm"
                    p={1}
                    border="none"
                    bg="transparent"
                    _focus={{ outline: 'none' }}
                  >
                    <option value="all">Show All</option>
                    <option value="only">Show Only</option>
                    <option value="hide">Hide</option>
                  </Box>
                </FormControl>
              </>
            )}

            <FormControl display="flex" alignItems="center" bg={hidePreliminary ? "orange.50" : "white"} p={2} rounded="md" borderWidth="1px" borderColor={hidePreliminary ? "orange.200" : "gray.200"} transition="all 0.2s" w="auto">
              <FormLabel htmlFor="hide-preliminary" mb="0" fontSize="sm" color={hidePreliminary ? "orange.800" : "gray.600"} fontWeight={hidePreliminary ? "bold" : "normal"} whiteSpace="nowrap">
                Hide Preliminary
              </FormLabel>
              <Switch 
                id="hide-preliminary" 
                colorScheme="orange"
                isChecked={hidePreliminary}
                onChange={(e) => setHidePreliminary(e.target.checked)}
              />
            </FormControl>
            <Button colorScheme="blue" onClick={() => { fetchReports(); fetchPatterns(); }} isLoading={loading || loadingPatterns}>
              Refresh
            </Button>
          </Flex>
        </Flex>

        <Tabs colorScheme="blue" variant="enclosed" index={activeTabIndex} onChange={(index) => setActiveTabIndex(index)}>
          <TabList>
            <Tab fontWeight="semibold">CPT Variance</Tab>
            <Tab fontWeight="semibold">Net New Codes</Tab>
            <Tab fontWeight="semibold">ICD-10 Variance</Tab>
            <Tab fontWeight="semibold">Denial Prevention</Tab>
            <Tab fontWeight="semibold">Coding Patterns</Tab>
            <Tab fontWeight="semibold">ICD-10 Search</Tab>
          </TabList>

          <TabPanels>
            {/* Tab 1: All Reports */}
            
            {/* Tab 1: CPT Variance */}
            <TabPanel px={0} py={6}>
              {loading ? (
                <Flex justify="center" align="center" p={20}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : reports.length === 0 ? (
                <Card><CardBody textAlign="center" py={10}><Text color="gray.500">No variance reports found.</Text></CardBody></Card>
              ) : (
                <Box>
                  <Card mb={6} bg="white" shadow="sm">
                    <CardBody>
                      <StatGroup>
                        <Stat>
                          <StatLabel color="gray.600">Total Cases Evaluated</StatLabel>
                          <StatNumber>{cptStats.total}</StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel color="orange.600">Actionable Variances</StatLabel>
                          <StatNumber color="orange.600">
                            {cptStats.actionable} <Text as="span" fontSize="md" color="orange.400">({Math.round((cptStats.actionable / cptStats.total) * 100)}%)</Text>
                          </StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel color="blue.600">Auto-Resolved</StatLabel>
                          <StatNumber color="blue.600">
                            {cptStats.autoResolved} <Text as="span" fontSize="md" color="blue.400">({Math.round((cptStats.autoResolved / cptStats.total) * 100)}%)</Text>
                          </StatNumber>
                        </Stat>
                        {cptStats.triaged > 0 && (
                          <Stat>
                            <StatLabel color="purple.600">Triage Need Review</StatLabel>
                            <StatNumber color="purple.600">
                              {Math.round(((cptStats.triaged - cptStats.autoResolved) / cptStats.triaged) * 100)}%
                            </StatNumber>
                          </Stat>
                        )}
                        <Stat>
                          <StatLabel color="green.600">Perfect Matches</StatLabel>
                          <StatNumber color="green.600">
                            {cptStats.perfect} <Text as="span" fontSize="md" color="green.400">({Math.round((cptStats.perfect / cptStats.total) * 100)}%)</Text>
                          </StatNumber>
                        </Stat>
                        {muscleGroupFilter === 'all' && cptStats.totalVariances > 0 && (
                          <Stat>
                            <StatLabel color="purple.600">95861/95870 Variances</StatLabel>
                            <StatNumber color="purple.600">
                              {cptStats.totalMuscleGroupVariances} <Text as="span" fontSize="md" color="purple.400">({Math.round((cptStats.totalMuscleGroupVariances / cptStats.totalVariances) * 100)}% of variances)</Text>
                            </StatNumber>
                          </Stat>
                        )}
                        {unitModifierFilter === 'all' && cptStats.totalVariances > 0 && (
                          <Stat>
                            <StatLabel color="teal.600">Unit/Mod Only Variances</StatLabel>
                            <StatNumber color="teal.600">
                              {cptStats.totalUnitModVariances} <Text as="span" fontSize="md" color="teal.400">({Math.round((cptStats.totalUnitModVariances / cptStats.totalVariances) * 100)}% of variances)</Text>
                            </StatNumber>
                          </Stat>
                        )}
                      </StatGroup>
                    </CardBody>
                  </Card>

                  {sortedCptReports.map(renderCptCard)}
                </Box>
              )}
            </TabPanel>

            {/* Tab 2: Net New Codes */}
            <TabPanel px={0} py={6}>
              {loading ? (
                <Flex justify="center" align="center" p={20}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : sortedNetNewReports.length === 0 ? (
                <Card><CardBody textAlign="center" py={10}><Text color="gray.500">No net new codes found.</Text></CardBody></Card>
              ) : (
                <Box>
                  <Card mb={6} bg="white" shadow="sm">
                    <CardBody>
                      <StatGroup>
                        <Stat>
                          <StatLabel color="orange.600">Cases with Net New Codes</StatLabel>
                          <StatNumber color="orange.600">{netNewStats.total}</StatNumber>
                        </Stat>
                      </StatGroup>
                    </CardBody>
                  </Card>
                  
                  {sortedNetNewReports.map(report => renderNetNewCard(report))}
                </Box>
              )}
            </TabPanel>

            {/* Tab 3: ICD-10 Variance */}
            <TabPanel px={0} py={6}>
              {loading ? (
                <Flex justify="center" align="center" p={20}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : reports.length === 0 ? (
                <Card><CardBody textAlign="center" py={10}><Text color="gray.500">No variance reports found.</Text></CardBody></Card>
              ) : (
                <Box>
                  <Card mb={6} bg="white" shadow="sm">
                    <CardBody>
                      <StatGroup>
                        <Stat>
                          <StatLabel color="gray.600">Total Cases Evaluated</StatLabel>
                          <StatNumber>{icd10Stats.total}</StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel color="orange.600">Cases with Variance</StatLabel>
                          <StatNumber color="orange.600">
                            {icd10Stats.variance} <Text as="span" fontSize="md" color="orange.400">({Math.round((icd10Stats.variance / icd10Stats.total) * 100)}%)</Text>
                          </StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel color="green.600">Perfect Matches</StatLabel>
                          <StatNumber color="green.600">
                            {icd10Stats.perfect} <Text as="span" fontSize="md" color="green.400">({Math.round((icd10Stats.perfect / icd10Stats.total) * 100)}%)</Text>
                          </StatNumber>
                        </Stat>
                      </StatGroup>
                    </CardBody>
                  </Card>

                  {sortedIcd10Reports.map(renderIcd10Card)}
                </Box>
              )}
            </TabPanel>

            {/* Tab 4: Denial Prevention */}
            <TabPanel px={0} py={6}>
              {loading ? (
                <Flex justify="center" align="center" p={20}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : sortedDenialReports.length === 0 ? (
                <Card><CardBody textAlign="center" py={10}><Text color="gray.500">No EOB denial analysis found.</Text></CardBody></Card>
              ) : (
                <Box>
                  <Card mb={6} bg="white" shadow="sm">
                    <CardBody>
                      <StatGroup>
                        <Stat>
                          <StatLabel color="gray.600">Cases with EOB Analysis</StatLabel>
                          <StatNumber>{denialStats.totalCases}</StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel color="gray.600">Denied Lines Evaluated</StatLabel>
                          <StatNumber>{denialStats.totalLinesEvaluated}</StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel color="green.600">Potential Mitigations</StatLabel>
                          <StatNumber color="green.600">
                            {denialStats.totalLinesPrevented} <Text as="span" fontSize="md" color="green.400">({Math.round((denialStats.totalLinesPrevented / denialStats.totalLinesEvaluated) * 100)}%)</Text>
                          </StatNumber>
                        </Stat>
                      </StatGroup>
                    </CardBody>
                  </Card>

                  {sortedDenialReports.map(renderDenialCard)}
                </Box>
              )}
            </TabPanel>

            {/* Tab 5: Coding Patterns */}
            <TabPanel px={0} py={6}>
              {loadingPatterns ? (
                <Flex justify="center" align="center" p={20}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : (
                <VStack spacing={8} align="stretch">
                  
                  {/* Section 1: Time-Based Coding */}
                  <Box>
                    <Heading size="md" mb={2} color="blue.800">Uncaptured Time Opportunities</Heading>
                    <Text color="gray.600" mb={4}>
                      Cases where professional time is documented, but time-based codes (G0453/95941) were either missed or billed inconsistently.
                    </Text>
                    
                    {patterns.time_based?.length === 0 ? (
                      <Card><CardBody><Text color="gray.500">No time-based coding discrepancies found.</Text></CardBody></Card>
                    ) : (
                      <Box bg="white" shadow="sm" rounded="lg" overflowX="auto" borderWidth="1px">
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Case ID</Th>
                              <Th>Payer</Th>
                              <Th>Carrier</Th>
                              <Th>Extracted Time</Th>
                              <Th>Claim Billed</Th>
                              <Th>Revedy Recommended</Th>
                              <Th textAlign="right">Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {patterns.time_based.map((item, idx) => {
                              const missedTime = item.prospect_g0453 === 0 && item.prospect_95941 === 0;
                              return (
                                <Tr key={idx} bg={missedTime ? "red.50" : "transparent"}>
                                  <Td fontWeight="medium">{item.external_case_id}</Td>
                                  <Td>{item.payer_type || 'Unknown'}</Td>
                                  <Td>{item.carrier_name || 'Unknown'}</Td>
                                  <Td fontStyle="italic" color="gray.600">{item.prof_time || 'Not found'}</Td>
                                  <Td>
                                    {missedTime ? (
                                      <Badge colorScheme="red">None Billed</Badge>
                                    ) : (
                                      <Flex gap={1}>
                                        {item.prospect_g0453 > 0 && <Badge colorScheme="blue">G0453 x {item.prospect_g0453}</Badge>}
                                        {item.prospect_95941 > 0 && <Badge colorScheme="purple">95941 x {item.prospect_95941}</Badge>}
                                      </Flex>
                                    )}
                                  </Td>
                                  <Td>
                                    <Flex gap={1}>
                                      {item.revedy_g0453 > 0 && <Badge colorScheme="blue">G0453 x {item.revedy_g0453}</Badge>}
                                      {item.revedy_95941 > 0 && <Badge colorScheme="purple">95941 x {item.revedy_95941}</Badge>}
                                    </Flex>
                                  </Td>
                                  <Td textAlign="right">
                                    <Button 
                                      size="xs" 
                                      colorScheme="blue" 
                                      variant="outline"
                                      as="a" href={`/coding-variance?caseId=${item.external_case_id}&claimType=${item.claim_type || 'pro'}`} target="_blank" rel="noopener noreferrer"
                                    >
                                      View Source
                                    </Button>
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* Section 2: Unit Alignment */}
                  <Box>
                    <Heading size="md" mb={2} color="purple.800">Unit Alignment (95870)</Heading>
                    <Text color="gray.600" mb={4}>
                      Comparison of billed units for 95870 against Revedy's recommendation based on the documented extremities.
                    </Text>
                    
                    {patterns.unit_alignment?.length === 0 ? (
                      <Card><CardBody><Text color="gray.500">No unit alignment discrepancies found.</Text></CardBody></Card>
                    ) : (
                      <Box bg="white" shadow="sm" rounded="lg" overflowX="auto" borderWidth="1px">
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Case ID</Th>
                              <Th>Payer</Th>
                              <Th>Carrier</Th>
                              <Th>Extracted Documentation</Th>
                              <Th>Claim Billed</Th>
                              <Th>Revedy Recommended</Th>
                              <Th>Status</Th>
                              <Th textAlign="right">Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {patterns.unit_alignment.map((item, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="medium">{item.external_case_id}</Td>
                                <Td>{item.payer_type || 'Unknown'}</Td>
                                <Td>{item.carrier_name || 'Unknown'}</Td>
                                <Td verticalAlign="top">
                                  <Text fontStyle="italic" color="gray.600" maxW="300px" isTruncated title={item.extremities_text}>
                                    {item.extremities_text || 'Not found'}
                                  </Text>
                                  {item.has_baseline_in_claim && (
                                    <Badge colorScheme="red" mt={1} fontSize="2xs">"Baseline" Noted on Claim</Badge>
                                  )}
                                </Td>
                                <Td>
                                  <Badge colorScheme={item.comparison_status === 'Prospect > Revedy' ? 'red' : 'gray'}>
                                    95870 x {item.prospect_units}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Badge colorScheme={item.comparison_status === 'Revedy > Prospect' ? 'blue' : 'gray'}>
                                    95870 x {item.revedy_units}
                                  </Badge>
                                </Td>
                                <Td>
                                  <Badge 
                                    colorScheme={
                                      item.comparison_status === 'Prospect > Revedy' ? 'red' : 
                                      item.comparison_status === 'Revedy > Prospect' ? 'blue' : 'green'
                                    }
                                    variant="outline"
                                  >
                                    {item.comparison_status}
                                  </Badge>
                                </Td>
                                <Td textAlign="right">
                                  <Button 
                                    size="xs" 
                                    colorScheme="blue" 
                                    variant="outline"
                                    as="a" href={`/coding-variance?caseId=${item.external_case_id}&claimType=${item.claim_type || 'pro'}`} target="_blank" rel="noopener noreferrer"
                                  >
                                    View Source
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* Section 3: Cranial Nerve & Larynx Bundling */}
                  <Box>
                    <Heading size="md" mb={2} color="teal.800">Cranial Nerve & Larynx Bundling (95865 / 95868)</Heading>
                    <Text color="gray.600" mb={4}>
                      Cases where the Larynx code (95865) was billed. Revedy's engine typically bundles this into the Cranial Nerve code (95868) when other cranial nerves are monitored.
                    </Text>
                    
                    {patterns.cranial_bundling?.length === 0 ? (
                      <Card><CardBody><Text color="gray.500">No cranial nerve bundling patterns found.</Text></CardBody></Card>
                    ) : (
                      <Box bg="white" shadow="sm" rounded="lg" overflowX="auto" borderWidth="1px">
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Case ID</Th>
                              <Th>Payer</Th>
                              <Th>Carrier</Th>
                              <Th>Extracted Documentation</Th>
                              <Th>Claim Billed</Th>
                              <Th>Revedy Recommended</Th>
                              <Th textAlign="right">Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {patterns.cranial_bundling.map((item, idx) => {
                              const isDoubleDip = item.prospect_95865 > 0 && item.prospect_95868 > 0;
                              return (
                                <Tr key={idx}>
                                  <Td fontWeight="medium">{item.external_case_id}</Td>
                                  <Td>{item.payer_type || 'Unknown'}</Td>
                                  <Td>{item.carrier_name || 'Unknown'}</Td>
                                  <Td fontStyle="italic" color="gray.600" maxW="300px" isTruncated title={item.cranial_text}>
                                    {item.cranial_text || 'Not found'}
                                  </Td>
                                  <Td>
                                    <Flex direction="column" gap={1}>
                                      {item.prospect_95865 > 0 && <Badge colorScheme="red">95865 x {item.prospect_95865}</Badge>}
                                      {item.prospect_95868 > 0 && <Badge colorScheme={isDoubleDip ? "red" : "gray"}>95868 x {item.prospect_95868}</Badge>}
                                    </Flex>
                                  </Td>
                                  <Td>
                                    <Flex direction="column" gap={1}>
                                      {item.revedy_95865 > 0 && <Badge colorScheme="blue">95865 x {item.revedy_95865}</Badge>}
                                      {item.revedy_95868 > 0 && <Badge colorScheme="blue">95868 x {item.revedy_95868}</Badge>}
                                      {item.revedy_95865 === 0 && item.revedy_95868 === 0 && <Text fontSize="xs" color="gray.500">None</Text>}
                                    </Flex>
                                  </Td>
                                  <Td textAlign="right">
                                    <Button 
                                      size="xs" 
                                      colorScheme="blue" 
                                      variant="outline"
                                      as="a" href={`/coding-variance?caseId=${item.external_case_id}&claimType=${item.claim_type || 'pro'}`} target="_blank" rel="noopener noreferrer"
                                    >
                                      View Source
                                    </Button>
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* Section 4: Uncaptured Revenue */}
                  <Box>
                    <Heading size="md" mb={2} color="green.800">Uncaptured Revenue (Missed Modalities)</Heading>
                    <Text color="gray.600" mb={4}>
                      Cases where Revedy's engine found explicit documentation for a modality (e.g., EEG, TOF, EMG) that the prospect completely missed billing.
                    </Text>
                    
                    {patterns.uncaptured_revenue?.length === 0 ? (
                      <Card><CardBody><Text color="gray.500">No uncaptured revenue patterns found.</Text></CardBody></Card>
                    ) : (
                      <Box bg="white" shadow="sm" rounded="lg" overflowX="auto" borderWidth="1px">
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Case ID</Th>
                              <Th>Payer</Th>
                              <Th>Carrier</Th>
                              <Th>Missed Code</Th>
                              <Th>Revedy Recommended</Th>
                              <Th>Clinical Evidence Found</Th>
                              <Th textAlign="right">Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {patterns.uncaptured_revenue.map((item, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="medium" verticalAlign="top">{item.external_case_id}</Td>
                                <Td verticalAlign="top">{item.payer_type || 'Unknown'}</Td>
                                <Td verticalAlign="top">{item.carrier_name || 'Unknown'}</Td>
                                <Td verticalAlign="top">
                                  <Badge colorScheme="red" variant="solid">{item.missed_code}</Badge>
                                </Td>
                                <Td verticalAlign="top">
                                  <Badge colorScheme="green">{item.missed_units} Units</Badge>
                                </Td>
                                <Td verticalAlign="top">
                                  <Text fontSize="sm" color="gray.700" maxW="400px" noOfLines={3} title={item.revedy_evidence}>
                                    {item.revedy_evidence || 'No evidence summary available.'}
                                  </Text>
                                </Td>
                                <Td textAlign="right" verticalAlign="top">
                                  <Button 
                                    size="xs" 
                                    colorScheme="blue" 
                                    variant="outline"
                                    as="a" href={`/coding-variance?caseId=${item.external_case_id}&claimType=${item.claim_type || 'pro'}`} target="_blank" rel="noopener noreferrer"
                                  >
                                    View Source
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* Section 5: ICD-10 Pattern Analysis */}
                  <Box>
                    <Heading size="md" mb={2} color="orange.800">ICD-10 Pattern Analysis</Heading>
                    <Text color="gray.600" mb={6}>
                      High-level comparison of how the prospect codes diagnoses versus Revedy's AI, highlighting cases where significant clinical complexity was missed.
                    </Text>

                    {patterns.icd10_stats && patterns.icd10_stats.total_evaluated > 0 && (
                      <Card mb={6} bg="orange.50" borderColor="orange.100" borderWidth="1px" shadow="sm">
                        <CardBody>
                          <StatGroup>
                            <Stat>
                              <StatLabel color="gray.600">Total Cases Evaluated</StatLabel>
                              <StatNumber color="orange.800">{patterns.icd10_stats.total_evaluated}</StatNumber>
                            </Stat>
                            <Stat>
                              <StatLabel color="gray.600">Primary Mismatch</StatLabel>
                              <StatNumber color="red.600">
                                {Math.round((patterns.icd10_stats.primary_mismatch / patterns.icd10_stats.total_evaluated) * 100)}%
                              </StatNumber>
                            </Stat>
                            <Stat>
                              <StatLabel color="gray.600">Revedy Found More Codes</StatLabel>
                              <StatNumber color="blue.600">
                                {Math.round((patterns.icd10_stats.revedy_suggested_more / patterns.icd10_stats.total_evaluated) * 100)}%
                              </StatNumber>
                            </Stat>
                            <Stat>
                              <StatLabel color="gray.600">Perfect Match</StatLabel>
                              <StatNumber color="green.600">
                                {Math.round((patterns.icd10_stats.perfect_match / patterns.icd10_stats.total_evaluated) * 100)}%
                              </StatNumber>
                            </Stat>
                          </StatGroup>
                        </CardBody>
                      </Card>
                    )}

                    <Heading size="sm" mb={3} color="gray.700">Uncaptured Clinical Complexity (Top Cases)</Heading>
                    {patterns.icd10_complexity?.length === 0 ? (
                      <Card><CardBody><Text color="gray.500">No significant complexity discrepancies found.</Text></CardBody></Card>
                    ) : (
                      <Box bg="white" shadow="sm" rounded="lg" overflowX="auto" borderWidth="1px">
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Case ID</Th>
                              <Th>Payer</Th>
                              <Th>Carrier</Th>
                              <Th>Code Counts</Th>
                              <Th>Missed Codes (Revedy Found)</Th>
                              <Th>AI Discrepancy Summary</Th>
                              <Th textAlign="right">Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {patterns.icd10_complexity?.map((item, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="medium" verticalAlign="top">{item.external_case_id}</Td>
                                <Td verticalAlign="top">{item.payer_type || 'Unknown'}</Td>
                                <Td verticalAlign="top">{item.carrier_name || 'Unknown'}</Td>
                                <Td verticalAlign="top" whiteSpace="nowrap">
                                  <VStack align="start" spacing={1}>
                                    <Badge colorScheme="gray">Prospect: {item.prospect_code_count}</Badge>
                                    <Badge colorScheme="blue">Revedy: {item.revedy_code_count}</Badge>
                                  </VStack>
                                </Td>
                                <Td verticalAlign="top">
                                  <Flex flexWrap="wrap" gap={1} maxW="250px">
                                    {item.missed_codes?.map((code, cIdx) => (
                                      <Badge key={cIdx} colorScheme="blue" variant="subtle">{code}</Badge>
                                    ))}
                                  </Flex>
                                </Td>
                                <Td verticalAlign="top">
                                  <Text fontSize="sm" color="gray.700" maxW="400px" noOfLines={4} title={item.ai_summary}>
                                    {item.ai_summary || 'No summary available.'}
                                  </Text>
                                </Td>
                                <Td textAlign="right" verticalAlign="top">
                                  <Button 
                                    size="xs" 
                                    colorScheme="blue" 
                                    variant="outline"
                                    as="a" href={`/coding-variance?caseId=${item.external_case_id}&claimType=${item.claim_type || 'pro'}`} target="_blank" rel="noopener noreferrer"
                                  >
                                    View Source
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>

                </VStack>
              )}
            </TabPanel>

            {/* Tab 6: ICD-10 Deep Dive */}
            <TabPanel px={0} py={6}>
              <Card mb={6}>
                <CardBody>
                  <Heading size="md" mb={4} color="blue.800">Search ICD-10 Discrepancies</Heading>
                  <Text color="gray.600" mb={6}>
                    Find cases where the prospect billed a specific code, but Revedy recommended a different one. 
                    Useful for identifying patterns of clinical inference vs. literal coding.
                  </Text>
                  
                  <Flex gap={4} align="flex-end">
                    <FormControl maxW="300px">
                      <FormLabel>Prospect Billed Code</FormLabel>
                      <Input 
                        placeholder="e.g., M54.17" 
                        value={prospectCode}
                        onChange={(e) => setProspectCode(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleIcd10Search()}
                      />
                    </FormControl>
                    <FormControl maxW="300px">
                      <FormLabel>Revedy Recommended Code</FormLabel>
                      <Input 
                        placeholder="e.g., M54.16" 
                        value={revedyCode}
                        onChange={(e) => setRevedyCode(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleIcd10Search()}
                      />
                    </FormControl>
                    <Button 
                      colorScheme="blue" 
                      onClick={handleIcd10Search}
                      isLoading={isSearchingIcd10}
                    >
                      Search
                    </Button>
                  </Flex>
                </CardBody>
              </Card>

              {isSearchingIcd10 ? (
                <Flex justify="center" align="center" p={20}>
                  <Spinner size="xl" color="blue.500" />
                </Flex>
              ) : hasSearchedIcd10 && icd10SearchResults.length === 0 ? (
                <Card>
                  <CardBody textAlign="center" py={10}>
                    <Text color="gray.500">No cases found matching those codes.</Text>
                  </CardBody>
                </Card>
              ) : icd10SearchResults.length > 0 ? (
                <Box bg="white" shadow="sm" rounded="lg" overflowX="auto" borderWidth="1px">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Case ID</Th>
                        <Th>Payer</Th>
                        <Th>Carrier</Th>
                        <Th>Prospect Codes</Th>
                        <Th>Revedy Codes</Th>
                        <Th>AI Discrepancy Summary</Th>
                        <Th textAlign="right">Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {icd10SearchResults.map((report) => {
                        const pCodes = (report.prospect_icd10_data || []).map(c => c.code || c);
                        const rCodes = report.revedy_icd10_data || [];
                        
                        return (
                          <Tr key={report.id}>
                            <Td fontWeight="medium" verticalAlign="top">{report.external_case_id}</Td>
                            <Td verticalAlign="top">{report.payer_type || 'Unknown'}</Td>
                            <Td verticalAlign="top">{report.carrier_name || 'Unknown'}</Td>
                            <Td verticalAlign="top">
                              <Flex direction="column" gap={1}>
                                {pCodes.map((code, idx) => (
                                  <Badge key={idx} colorScheme={prospectCode && code.replace('.','').includes(prospectCode.replace('.','')) ? "yellow" : "gray"}>
                                    {code}
                                  </Badge>
                                ))}
                              </Flex>
                            </Td>
                            <Td verticalAlign="top">
                              <Flex direction="column" gap={1}>
                                {rCodes.map((code, idx) => (
                                  <Badge key={idx} colorScheme={revedyCode && code.replace('.','').includes(revedyCode.replace('.','')) ? "blue" : "gray"}>
                                    {code}
                                  </Badge>
                                ))}
                              </Flex>
                            </Td>
                            <Td verticalAlign="top">
                              <VStack align="start" spacing={2}>
                                <Text fontSize="sm">{report.icd10_ai_evaluation?.discrepancy_summary || 'No summary available'}</Text>
                                {report.icd10_ai_evaluation?.clinical_evidence_for_prospect && report.icd10_ai_evaluation.clinical_evidence_for_prospect !== 'N/A' && (
                                  <Box bg="yellow.50" p={2} rounded="md" w="100%">
                                    <Text fontSize="xs" fontWeight="bold" color="yellow.800">Prospect Evidence:</Text>
                                    <Text fontSize="xs" color="yellow.800">{report.icd10_ai_evaluation.clinical_evidence_for_prospect}</Text>
                                  </Box>
                                )}
                              </VStack>
                            </Td>
                            <Td textAlign="right" verticalAlign="top">
                              <Button 
                                size="xs" 
                                colorScheme="blue" 
                                variant="outline"
                                as="a" href={`/coding-variance?caseId=${report.external_case_id}&claimType=${report.claim_type || 'pro'}`} target="_blank" rel="noopener noreferrer"
                              >
                                View Details
                              </Button>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              ) : null}
            </TabPanel>
          </TabPanels>
        </Tabs>

      </Container>
    </Box>
  );
}